"use client";

import type { CtlBranch } from "@/api/useGetCtlMapApi";
import {
  CTL_TABLE_COLUMNS,
  CTL_TABLE_SKELETON_COUNT,
} from "../constants";

interface CtlTableProps {
  branches: CtlBranch[];
  isLoading: boolean;
  selectedId: number | null;
  onSelect: (id: number) => void;
}

export default function CtlTable({ branches, isLoading, selectedId, onSelect }: CtlTableProps) {
  return (
    <div className="w-full h-full overflow-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
      <table className="w-full h-full text-sm bg-white">
        <thead className="sticky top-0 z-10">
          <tr className="border-b border-slate-100 bg-slate-50 text-slate-500 text-sm uppercase tracking-wider">
            {CTL_TABLE_COLUMNS.map((col) => (
              <th key={col.key} className="px-4 py-3 text-center font-semibold whitespace-nowrap">
                {col.key === "linkUrl" ? <span className="sr-only">{col.label}</span> : col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            Array.from({ length: CTL_TABLE_SKELETON_COUNT }).map((_, i) => (
              <tr key={i} className="bg-white border-b border-slate-100">
                {Array.from({ length: CTL_TABLE_COLUMNS.length }).map((_, j) => (
                  <td key={j} className="px-4 py-3">
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </td>
                ))}
              </tr>
            ))
          ) : branches.length === 0 ? (
            <tr>
              <td colSpan={CTL_TABLE_COLUMNS.length} className="px-4 py-16 text-center text-sm text-slate-500">
                등록된 지점이 없습니다.
              </td>
            </tr>
          ) : (
            branches.map((branch) => (
              <tr
                key={branch.id}
                onClick={() => onSelect(branch.id)}
                className={`cursor-pointer border-b border-slate-100 last:border-b-0 transition-colors ${
                  selectedId === branch.id ? "bg-blue-50" : "bg-white hover:bg-slate-50"
                }`}
              >
                <td className="px-4 py-3 text-center text-sm text-slate-700 font-medium whitespace-nowrap">
                  {branch.name}
                </td>
                <td className="px-4 py-3 text-center text-sm text-slate-600 whitespace-nowrap">
                  {branch.region}
                </td>
                <td className="px-4 py-3 text-center text-sm text-slate-600">
                  {branch.address}
                </td>
                <td className="px-4 py-3 text-center text-sm whitespace-nowrap">
                  {branch.linkUrl ? (
                    <a
                      href={branch.linkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`${branch.name} 링크 열기`}
                      className="inline-flex items-center justify-center text-slate-400 hover:text-slate-500 text-base leading-none"
                    >
                      <span aria-hidden="true">↗</span>
                    </a>
                  ) : (
                    <span className="text-slate-400">-</span>
                  )}
                </td>
              </tr>
            ))
          )}
          {!isLoading && branches.length > 0 && (
            <tr className="h-full">
              <td colSpan={CTL_TABLE_COLUMNS.length} className="p-0 bg-white" />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
